import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
  Legend as RechartsLegend, ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import PropTypes from 'prop-types';
import "./ReportadasAdmin.css";
import Permiso from "../../router/Permiso";
import { View, Phone } from "lucide-react";
import { SaleService } from "../../services/SaleService";
import { useNavigate } from "react-router-dom";
import Input from "../../components/Input";
import 'react-circular-progressbar/dist/styles.css';
import RadialSeparators from "../../components/RadialSeparators";
import { AuthContext } from "../../context/AuthContext";

const ReportadasAdmin = ({ refreshTrigger = 0 }) => {
  const { token } = useContext(AuthContext);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [codeSale, setCodeSale] = useState("");
  const [clientName, setClientName] = useState("");
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ventas, setVentas] = useState([]);
  const [productivityList, setProductivityList] = useState([]);
  const [percentStatistic, setPercentStatistic] = useState(0);
  const navigate = useNavigate();
  const COLORS = ['#22b04c', '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948', '#b07aa1'];

  const getStatistics = useCallback(async () => {
    const params = {
      token,
      fechaInicio: fechaInicio && fechaInicio.trim() !== "" ? fechaInicio : null,
      fechaFin: fechaFin && fechaFin.trim() !== "" ? fechaFin : null
    };
    const result = await SaleService.getStatistics(params);

    const percent = result.totalInstaladas > 0 ? Math.round((result.conSeguimiento / result.totalInstaladas) * 100) : 0;
    setPercentStatistic(percent);
    const productivityList = result.detallePorUsuario.map(u =>
      ({ name: u.nombre, cantidad: u.ventas_con_seguimiento }));
    setProductivityList(productivityList)

  }, [token, fechaInicio, fechaFin]);

  const exportarExcel = async () => {
    try {
      const filtros = {
        desde: fechaInicio,
        hasta: fechaFin,
        dni: codeSale,
        nombre: clientName,
        reportada: true
      };

      const blob = await SaleService.exportarVentasReportadasExcel(token, filtros);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "listado_venta_reportadas.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
    }
  };

  const fetchVentas = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { ventas, totalPaginas, totalRegistros } = await SaleService.fetchVentasReported({
        token,
        pagina: currentPage,
        pageSize: 20,
        desde: fechaInicio,
        hasta: fechaFin,
        dni: codeSale,
        nombre: clientName,
        reported: true
      });

      setVentas(ventas);
      setTotalPages(totalPaginas);
      setTotalRegistros(totalRegistros);
    } catch (err) {
      setError("Error al cargar las ventas");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, fechaInicio, fechaFin, codeSale, clientName]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchVentas(), getStatistics()]);
    };
    loadData();
  }, [fetchVentas, getStatistics, refreshTrigger]);

  return (
    <div className="ventas-admin-content">
      <div className="ventas-admin-tab-content">
        <div className="ventas-admin-subtitle">
          Reportadas
        </div>
        <div className="flex w-full">
          <div className="w-20">


          </div>
          <div className="w-20">
            <div className="ventas-admin-subtitle pb-10">
              Avance de Gestión
            </div>
            <CircularProgressbarWithChildren
              value={percentStatistic}
              text={`${percentStatistic}%`}
              strokeWidth={10}
              styles={buildStyles({
                strokeLinecap: "butt",
                pathColor: "#8a6fcb",
                textColor: "black"
              })}
            >
              <RadialSeparators
                count={12}
                style={{
                  background: "#fff",
                  width: "2px",
                  height: `${10}%`
                }}
              />
            </CircularProgressbarWithChildren>
          </div>
          <div className="w-40">
            <div className="ventas-admin-subtitle pb-10">
              Productividad
            </div>
            {
              productivityList.length > 0 && (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={productivityList}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <RechartsLegend />
                    <Bar dataKey="cantidad" name="Ventas Reportadas con seguimiento">
                      {productivityList.map((entry, index) => (
                        <Cell
                          key={`cell-${entry.name}-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                      <LabelList dataKey="cantidad" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )
            }
          </div>
        </div>

        <div className="ventas-table-header">
          <h2 className="ventas-table-title">Lista de reportadas </h2>
          <div className="productos-actions">
            <Permiso permiso="export_reported">
              <button onClick={exportarExcel} className="btn-exportar">
                Exportar a Excel
              </button>
            </Permiso>
          </div>
        </div>

        <div className="usuarios-filtros">
          <Input title="Código de venta:" value={codeSale} setValue={setCodeSale} placeholder="Ingrese número" className="filtro-item" />
          <Input title="Buscar por nombre:" value={clientName} setValue={setClientName} placeholder="Ingrese nombre del cliente" className="filtro-item" />
          <div className="filtro-item">
            <label htmlFor="fecha-inicio">Fecha Inicio:</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>
          <div className="filtro-item">
            <label htmlFor="fecha-fin">Fecha Fin:</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>
          <div className="filtro-item">
            <button onClick={() => {
              if (codeSale !== "" || clientName !== "" || fechaInicio !== "" || fechaFin !== "") {
                fetchVentas();
              }
            }} className="btn-filtrar">
              Buscar
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">Cargando ventas...</div>
        ) : (
          <>
            <div className="ventas-table-wrapper">
              <table className="ventas-table">
                <thead>
                  <tr>
                    <th>Codigo</th>
                    <th>Vendedor</th>
                    <th>Cliente</th>
                    <th>F. Venta</th>
                    <th>F. Gestión</th>
                    <th>Estado</th>
                    <th>Temático</th>
                    <th>Llamada</th>
                    <th>Instalado</th>
                    <th># Contac</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ventas.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-data">
                        <div className="no-data-content">
                          <p>No hay ventas reportadas</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    ventas.map((venta) => {
                      const following = venta.item_correlativo !== null;
                      let llamadaLabel = '';
                      let instaladoLabel = '';
                      if (following) {
                        llamadaLabel = venta.llamada === 1 ? 'No contacto' : 'Contacto Efectivo';
                        instaladoLabel = venta.instalado === 1 ? 'Si' : 'No';
                      }
                      const estadoCliente = venta.estado_cliente;
                      let estadoColor = '#555';
                      if (estadoCliente === 'INSTALADO') estadoColor = '#22b04c';
                      else if (estadoCliente === 'REPORTADO') estadoColor = '#f28e2b';
                      else if (estadoCliente === 'ANULADA') estadoColor = '#e15759';
                      return (
                        <tr key={venta.id}>
                          <td>{venta.cod_cliente}</td>
                          <td>
                            {venta.nombre_vendedor} {venta.apellido_vendedor}
                          </td>
                          <td>
                            {venta.cliente_nombre}
                          </td>
                          <td>
                            {venta.fecha_precliente
                              ? new Date(venta.fecha_precliente).toLocaleDateString()
                              : ''}
                          </td>
                          <td>{following ? new Date(venta.fecha_seguimiento).toLocaleDateString() : ''}</td>
                          <td>
                            <span style={{ fontWeight: 'bold', textTransform: 'uppercase', color: estadoColor }}>
                              {estadoCliente}
                            </span>
                          </td>
                          <td>{following ? venta.tematico : ''}</td>
                          <td>{llamadaLabel}</td>
                          <td>{instaladoLabel}</td>
                          <td>{following ? venta.item_correlativo : ''}</td>
                          <td className="ventas-acciones">
                            <Permiso permiso="view_reported">
                              <button
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  cursor: "pointer",
                                  outline: "inherit",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "inherit",
                                }}
                                onClick={() => navigate(`/admin/reportadas/registrar/${venta.id}/only`)}
                              >
                                <View size={20} />
                              </button>
                            </Permiso>
                            {
                              venta.estado_cliente === "REPORTADO" && (
                                <Permiso permiso="add_seguimiento_reported">
                                  <button
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      cursor: "pointer",
                                      outline: "inherit",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "inherit",
                                    }}
                                    onClick={() => {
                                      navigate(`/admin/reportadas/registrar/${venta.id}`);
                                    }}
                                  >
                                    <Phone size={20} />
                                  </button>
                                </Permiso>
                              )
                            }
                          </td>
                        </tr>
                      );
                    })

                  )}
                </tbody>
              </table>
            </div>
            <span style={{ marginLeft: "12px", fontSize: "14px", color: "#555" }}>
              {" "}
              Total de registros: {totalRegistros}{" "}
            </span>
            {/* Paginación */}
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </button>
              <span>
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Siguiente
              </button>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

ReportadasAdmin.propTypes = {
  refreshTrigger: PropTypes.number
}

export default ReportadasAdmin;
